import { pxToEm } from 'util/pxToEm';
import { Box, Typography, useTheme } from '@mui/material';
import { DeleteIcon } from 'components/icons';
import { Button, Modal, ModalProps, TextInput, Select } from 'components/ui';
import { useProfile } from 'hooks';
import { debounce } from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { UserSummary, useSchoolsApi, useUsersApi } from 'services/api';
import { FindUserForApprovalResponse } from 'services/api/apis/users/dto/find-user-for-approval.dto';
import { Result } from 'services/api/result.type';
import { useSnackbar } from 'notistack';
import { SchoolApprovalTitleDto } from 'services/api/apis/schools/dto/school-approval-title.dto';
import { ApprovalSequenceDto } from 'services/api/apis/schools/dto/approval-title-with-sequences.dto';

export interface AddApprovalSequenceModalProps
  extends Pick<ModalProps, 'open' | 'onClose'> {
  onSuccess?: () => void;
  schoolId: string;
  editingSequence?: ApprovalSequenceDto | null;
}

export const AddApprovalSequenceModal = ({
  open,
  onClose,
  onSuccess,
  schoolId,
  editingSequence,
}: AddApprovalSequenceModalProps): ReactElement => {
  const theme = useTheme();
  const { profile } = useProfile();
  const { enqueueSnackbar } = useSnackbar();
  const schoolsApi = useSchoolsApi();

  const domainName = profile?.school?.domainName ?? '';


  const [email, setEmail] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [user, setUser] = useState<UserSummary | null>(null);
  const [error, setError] = useState('');
  const [selectedApprovalTitleId, setSelectedApprovalTitleId] = useState<SchoolApprovalTitleDto['id']>('');
  const [schoolApprovalTitles, setSchoolApprovalTitles] = useState<SchoolApprovalTitleDto[]>([]);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(false);


  const handleSave = async () => {
    setLoading(true);
    if (editingSequence) {
      // Update existing sequence
      const result = await schoolsApi.updateApprovalSequence(editingSequence.id, {
        schoolApprovalTitleId: selectedApprovalTitleId,
      });
      if (result.success) {
        enqueueSnackbar('Approval sequence updated successfully', { variant: 'success' });
        onSuccess?.();
      } else {
        enqueueSnackbar(result.error?.message || 'Failed to update approval sequence', { variant: 'error' });
      }
    } else {
      // Add new sequence
      const result = await schoolsApi.addApprovalSequence(schoolId, {
        emailAddress: email,
        schoolApprovalTitleId: selectedApprovalTitleId,
      });
      if (result.success) {
        enqueueSnackbar('Approval sequence added successfully', { variant: 'success' });
        onSuccess?.();
      } else {
        enqueueSnackbar(result.error?.message || 'Failed to add approval sequence', { variant: 'error' });
      }
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    if (!editingSequence) return;

    setIsDeleting(true);
    try {
      const result = await schoolsApi.removeApprovalSequence(schoolId, editingSequence.id);
      if (result.success) {
        enqueueSnackbar('Approval sequence removed successfully', { variant: 'success' });
        onSuccess?.();
      } else {
        enqueueSnackbar(result.error?.message || 'Failed to remove approval sequence', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to remove approval sequence', { variant: 'error' });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const handleClose = () => {
    setEmail('');
    setSelectedApprovalTitleId('');
    setHasSearched(false);
    setError('');
    setShowDeleteConfirmation(false);
    onClose?.();
  };

  const usersApi = useUsersApi();


  const handleSearchChange = useCallback(
    debounce(async (email: string) => {
      if (!email.endsWith(domainName)) {
        setError(
          `Email address not valid. Please use an email that ends with ${domainName}`,
        );
        return;
      }

      if (error) setError('');

      const result: Result<FindUserForApprovalResponse> = await usersApi.findUserForApprovalSequence(email, schoolId);
      if (result?.success) {
        const userData = result.value;

        // Check if user is already in sequence
        if (userData.isAlreadyInSequence) {
          setError('This user is already added to the approval sequence');
          setUser(null);
        } else {
          setUser({
            id: userData.id,
            firstName: userData.firstName,
            lastName: userData.lastName,
            role: userData.role as ('user' | 'leader' | 'admin' | 'executive-admin' | 'master' | 'approver')[],
            schoolEmails: [],
            isActive: true,
          });
          setError('');
        }
      } else {
        setUser(null);
        setError(result.error?.message || 'User not found or does not have required permissions');
      }

      setHasSearched(true);
    }, 200),
    [usersApi, profile?.primaryEmail, domainName, schoolId],
  );

  const getSchoolApprovalTitles = async () => {
    const result = await schoolsApi.getSchoolApprovalTitles(schoolId);
    if (result.success)
      setSchoolApprovalTitles(result.value);

    if (!result.success) {
      enqueueSnackbar(result.error?.message || 'Failed to get school approval titles', { variant: 'error' });
      return;
    }
  };

  useEffect(() => {
    if (!email) {
      setUser(null);
      setError('');
      return;
    }

    void handleSearchChange(email);
  }, [email, handleSearchChange]);

  useEffect(() => {
    getSchoolApprovalTitles();
  }, [schoolId]);

  useEffect(() => {
    if (editingSequence) {
      setEmail(editingSequence.emailAddress);
      setSelectedApprovalTitleId(editingSequence.schoolApprovalTitleId);
      setUser({
        id: editingSequence.userId,
        firstName: editingSequence.user.firstName,
        lastName: editingSequence.user.lastName,
        role: [],
        schoolEmails: [],
        isActive: true,
      });
      setHasSearched(true);
    }
  }, [editingSequence]);

  return (
    <Modal
      contentStyle={{ paddingTop: '10px' }}
      headerStyle={{ justifyContent: editingSequence ? 'left' : 'center' }}
      title={
        showDeleteConfirmation
          ? 'Confirm Delete'
          : editingSequence
            ? `Edit ${editingSequence.user.firstName} ${editingSequence.user.lastName}`
            : 'Add Sequence Approver'
      }
      open={open}
      onClose={handleClose}
    >
      {showDeleteConfirmation ? (
        <Box sx={{ width: 400, maxWidth: '80vw' }}>
          <Typography fontSize={pxToEm(16)} mb={16}>
            Are you sure you want to remove {editingSequence?.user.firstName} {editingSequence?.user.lastName} from the approval sequence?
          </Typography>
          <Typography fontSize={pxToEm(14)} color="text.secondary" mb={24}>
            This action cannot be undone.
          </Typography>
          <Box display="flex" gap={16}>
            <Button
              inverted
              onClick={() => setShowDeleteConfirmation(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="vibrant"
              color="error"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Removing...' : 'Remove'}
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <Box sx={{ width: 500, maxWidth: '80vw' }}>
            {!editingSequence && (
              <Box width="100%" maxWidth={450}>
                <Box
                  display="flex"
                  alignItems="end"
                  gap={{ xs: 1, md: 0 }}
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  width="100%"
                >
                  <Box flex={1} width={300}>
                    <TextInput
                      label="Email Address"
                      width="100%"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                    />
                  </Box>
                  <Box
                    pl={8}
                    minWidth={150}
                    width={{ xs: '100%', sm: 'auto' }}
                    mt={{ xs: 4, md: 2 }}
                  >
                    {hasSearched &&
                      (user ? (
                        <Typography>{`${user.firstName} ${user.lastName}`}</Typography>
                      ) : (
                        <Typography color="error" fontWeight={600}>
                          User not found
                        </Typography>
                      ))}
                  </Box>
                </Box>
                {error && (
                  <Typography
                    style={{
                      color: theme.palette.error.main,
                      fontWeight: 400,
                      fontSize: 14,
                    }}
                  >
                    {error}
                  </Typography>
                )}
              </Box>
            )}

            {editingSequence && (
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  gap={8}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setShowDeleteConfirmation(true)}
                  sx={{
                    color: '#d32f2f',
                    '&:hover': {
                      color: '#b71c1c',
                    },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                  <Typography
                    fontSize={pxToEm(14)}
                    sx={{
                      textDecoration: 'underline',
                      color: 'inherit',
                    }}
                  >
                    Remove
                  </Typography>
                </Box>
              </Box>
            )}

            {/* GROUP */}
            <Box mt={16} width={300}>
              <Select
                label="Group"
                value={selectedApprovalTitleId}
                onChange={e => setSelectedApprovalTitleId(e.target.value)}
                placeholder="Select group"
                required
              >
                {schoolApprovalTitles.map(option => (
                  <option key={option.id} value={option.id}>
                    {option.title}
                  </option>
                ))}
              </Select>
            </Box>
          </Box>

          <Box mt={30} display="flex">
            <Box mr={30}>
              <Button minWidth={100} inverted onClick={handleClose}>
                Cancel
              </Button>
            </Box>
            <Button
              minWidth={100}
              variant="vibrant"
              disabled={!selectedApprovalTitleId || loading}
              onClick={handleSave}
            >
              Save
            </Button>
          </Box>
        </>
      )}
    </Modal>
  );
};
